import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaEdit, FaMobileAlt, FaPlus, FaTrash } from 'react-icons/fa';
import VisitMobileConfigApi from '../../../api/VisitMobileConfigApi';

const DEFAULT_VISIT_PLAN_MAP_CONFIG = {
  zoom: 14,
  max_height: 400,
  coord_source_mode: 'split',
  lat_field: 'visit_location_latitude',
  lng_field: 'visit_location_longitude',
  connect: 'directions',
  target_field_name: 'visit_plan_id',
  target_object_name: 'visit_stop',
  source_field_name: 'id',
  order_field: 'sort_order_number',
  order_by: 'order_field',
  sort_order: 'ASC',
  label_field: 'name',
  max_stops: 50,
  fields: [
    { field: 'name', order: 1, display_type: 'highlight' },
    { field: 'status', order: 2 },
    {
      order: 3,
      fields: ['street', 'city', 'state', 'zip_postal_code', 'country'],
      formula_field: 'address',
      formula_field_label: 'Address',
    },
  ],
};

const emptyForm = () => ({
  id: null,
  object_name: 'visit_plan',
  config_key: 'visit_plan_map',
  config_type: 'map',
  is_active: true,
  configText: JSON.stringify(DEFAULT_VISIT_PLAN_MAP_CONFIG, null, 2),
});

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

const VisitMobileConfigManager = () => {
  const api = useMemo(() => new VisitMobileConfigApi(), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.list(true);
      if (res?.data?.success) {
        setRows(res.data.data || []);
      } else {
        setError(res?.data?.message || 'Failed to load visit mobile configs');
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load visit mobile configs'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setShowForm(true);
    setSuccess('');
    setError('');
  };

  const openEdit = row => {
    let configText = '{}';
    try {
      const cfg =
        typeof row.config === 'string' ? JSON.parse(row.config) : row.config;
      configText = JSON.stringify(cfg || {}, null, 2);
    } catch {
      configText = '{}';
    }
    setForm({
      id: row.id,
      object_name: row.object_name || '',
      config_key: row.config_key || '',
      config_type: row.config_type || '',
      is_active: row.is_active !== false,
      configText,
    });
    setShowForm(true);
    setSuccess('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(form.configText || '{}');
    } catch {
      setError('Config must be valid JSON.');
      setSaving(false);
      return;
    }

    try {
      const res = await api.save({
        id: form.id,
        object_name: form.object_name,
        config_key: form.config_key,
        config_type: form.config_type || null,
        is_active: form.is_active,
        config: parsedConfig,
      });
      if (!res?.data?.success) {
        setError(res?.data?.message || 'Failed to save configuration');
        return;
      }
      setSuccess('Configuration saved.');
      setShowForm(false);
      await loadRows();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save configuration'
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setError('');
    try {
      const res = await api.deleteConfig(deleteTarget.id);
      if (!res?.data?.success) {
        setError(res?.data?.message || 'Failed to delete configuration');
        return;
      }
      setSuccess('Configuration deleted.');
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to delete configuration'
      );
    }
  };

  return (
    <Container fluid className='p-4'>
      <Row className='mb-3 align-items-center'>
        <Col>
          <h4 className='mb-1 d-flex align-items-center gap-2'>
            <FaMobileAlt /> Visit Mobile Config
          </h4>
          <p className='text-muted mb-0'>
            Map, route, and mobile layout settings for Visit Manager (e.g.{' '}
            <code>visit_plan_map</code> on <code>visit_plan</code>).
          </p>
        </Col>
        <Col xs='auto'>
          <Button variant='primary' onClick={openCreate}>
            <FaPlus className='me-1' /> New config
          </Button>
        </Col>
      </Row>

      {error ? <Alert variant='danger'>{error}</Alert> : null}
      {success ? <Alert variant='success'>{success}</Alert> : null}

      <Card>
        <Card.Body>
          {loading ? (
            <div className='text-center py-4'>
              <Spinner animation='border' size='sm' className='me-2' />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <Alert variant='info' className='mb-0'>
              No visit mobile configs yet. Create <code>visit_plan_map</code> for{' '}
              <code>visit_plan</code> to power mobile routes and stop lists.
            </Alert>
          ) : (
            <Table responsive hover className='mb-0 align-middle'>
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Config key</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th>Updated</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td>
                      <code>{row.object_name}</code>
                    </td>
                    <td>
                      <code>{row.config_key}</code>
                    </td>
                    <td>{row.config_type || '—'}</td>
                    <td>
                      {row.is_active ? (
                        <Badge bg='success'>Active</Badge>
                      ) : (
                        <Badge bg='secondary'>Inactive</Badge>
                      )}
                    </td>
                    <td>{formatDate(row.lastmodifieddate)}</td>
                    <td className='text-end'>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        className='me-1'
                        onClick={() => openEdit(row)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-danger'
                        onClick={() => setDeleteTarget(row)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showForm} onHide={() => setShowForm(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {form.id ? 'Edit visit mobile config' : 'New visit mobile config'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className='mb-3'>
                  <Form.Label>Object name</Form.Label>
                  <Form.Control
                    value={form.object_name}
                    onChange={e =>
                      setForm(f => ({ ...f, object_name: e.target.value }))
                    }
                    placeholder='visit_plan'
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className='mb-3'>
                  <Form.Label>Config key</Form.Label>
                  <Form.Control
                    value={form.config_key}
                    onChange={e =>
                      setForm(f => ({ ...f, config_key: e.target.value }))
                    }
                    placeholder='visit_plan_map'
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className='mb-3'>
                  <Form.Label>Config type</Form.Label>
                  <Form.Control
                    value={form.config_type}
                    onChange={e =>
                      setForm(f => ({ ...f, config_type: e.target.value }))
                    }
                    placeholder='map'
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className='mb-3'>
              <Form.Check
                type='switch'
                id='visit-mobile-config-active'
                label='Active'
                checked={form.is_active}
                onChange={e =>
                  setForm(f => ({ ...f, is_active: e.target.checked }))
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Config JSON</Form.Label>
              <Form.Control
                as='textarea'
                rows={16}
                value={form.configText}
                onChange={e =>
                  setForm(f => ({ ...f, configText: e.target.value }))
                }
                className='font-monospace'
                spellCheck={false}
              />
              <Form.Text className='text-muted'>
                Use <code>target_field_name: &quot;visit_plan_id&quot;</code> on{' '}
                <code>visit_stop</code>.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete configuration?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete <code>{deleteTarget?.config_key}</code> for{' '}
          <code>{deleteTarget?.object_name}</code>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VisitMobileConfigManager;
